import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { auth } from './firebase';
import {
  obtenerLista, actualizarLista as actualizarListaApi,
  agregarAutoALista as agregarAutoAListaApi,
  quitarAutoDeLista as quitarAutoDeListaApi,
  agregarSeguimientoPrecio as agregarSeguimientoPrecioApi,
  eliminarSeguimientoPrecio as eliminarSeguimientoPrecioApi,
  guardarSubcategoriaProyecto as guardarSubcategoriaProyectoApi,
  consultarProyectoIA as consultarProyectoIAApi,
  listarHistorialIAProyecto as listarHistorialIAProyectoApi,
} from './api/listas';
import { listarCatalogo, obtenerHistorialPrecios, resolverModeloActual } from './api/catalogo';
import {
  sugerirCompetidores, agregarCompetidor, quitarCompetidor, guardarAlertaConfigProyecto,
  agregarOfertaFinanciamiento, eliminarOfertaFinanciamiento, listarOfertasFinanciamiento,
  obtenerComparadorFinanciero, listarMisAlertas,
} from './api/alertas';
import { obtenerValueScoreDetalle, simularValueScore, guardarPesosProyecto } from './api/valueScore';
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Line, LineChart
} from 'recharts';
import './DetalleLista.css';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  Folder, Pencil, Search as SearchIcon, Plus, Trash2,
  Eye, EyeOff, BarChart3, TrendingUp, X, Save, Tag, Download, Clock, FileSpreadsheet, Bot,
  Radar, Sparkles, Landmark, Gauge, ArrowRight,
} from 'lucide-react';
import { getFunctions, httpsCallable } from "firebase/functions";
import ComparadorModelos from './Comparadormodelos';
import CarLoader from './components/CarLoader';

const functions = getFunctions();
const consultarClaudePM = httpsCallable(functions, "consultarClaudePM", { timeout: 170000 });
const uid = auth.currentUser?.uid ?? null;

const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c', '#d0ed57'];

// ── Tab IA del proyecto: consultas con contexto propio (modelos del
// proyecto, mercado en general, u otros modelos que se nombren), con el
// hilo de preguntas/respuestas guardado por proyecto — separado del
// historial general de IA de la cuenta. ──
function ProyectoIA({ listaId }) {
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  const cargarHistorial = async () => {
    setCargando(true);
    try {
      const h = await listarHistorialIAProyectoApi(listaId);
      setHistorial(h);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo cargar el historial de IA');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarHistorial(); }, [listaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const enviarConsulta = async (textoForzado) => {
    const texto = (textoForzado ?? prompt).trim();
    if (!texto || enviando) return;
    setEnviando(true);
    setError('');
    try {
      // Últimos intercambios como contexto conversacional para la IA.
      const historialContexto = historial.slice(-6).flatMap(h => ([
        { role: 'user', content: h.prompt },
        { role: 'assistant', content: h.response },
      ]));
      const res = await consultarProyectoIAApi(listaId, texto, historialContexto);
      setHistorial(prev => [...prev, {
        id: `local-${Date.now()}`, prompt: texto, response: res.response, createdAt: res.createdAt,
      }]);
      setPrompt('');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Error al consultar la IA');
      toast.error('Error al consultar la IA');
    } finally {
      setEnviando(false);
    }
  };

  const SUGERENCIAS = [
    '¿Cómo se posicionan en precio los modelos de este proyecto frente al mercado?',
    '¿Cuáles tuvieron mayores variaciones de precio últimamente?',
    'Dame un resumen estratégico de este proyecto para presentar.',
  ];

  return (
    <div className="dl-ia-proyecto">
      <div className="dl-ia-proyecto-header">
        <Bot size={16} />
        <div>
          <div className="dl-ia-proyecto-title">Asistente IA de este proyecto</div>
          <p className="dl-ia-proyecto-sub">
            Pregunta sobre los modelos de este proyecto, sobre el mercado en general, o sobre cualquier otro modelo — las respuestas quedan guardadas acá.
          </p>
        </div>
      </div>

      {cargando ? (
        <div className="dl-ia-proyecto-vacio"><CarLoader label="Cargando historial…" /></div>
      ) : historial.length === 0 ? (
        <div className="dl-ia-proyecto-vacio">
          <p>Todavía no hay preguntas en este proyecto.</p>
          <div className="dl-ia-proyecto-sugerencias">
            {SUGERENCIAS.map(s => (
              <button key={s} className="dl-btn" onClick={() => enviarConsulta(s)} disabled={enviando}>{s}</button>
            ))}
          </div>
        </div>
      ) : (
        <div className="dl-ia-proyecto-hilo">
          {historial.map(h => (
            <div key={h.id} className="dl-ia-proyecto-exchange">
              <div className="dl-ia-proyecto-pregunta">{h.prompt}</div>
              <div className="ai-result-card" style={{ marginTop: 6 }}>
                <div className="ai-result-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{h.response}</ReactMarkdown>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="dl-error">⚠️ {error}</div>}

      <div className="dl-ia-proyecto-input-row">
        <textarea
          className="dl-ia-proyecto-input"
          placeholder="Preguntá algo sobre este proyecto o el mercado…"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); enviarConsulta(); }
          }}
        />
        <button className="dl-btn dl-btn--primary" onClick={() => enviarConsulta()} disabled={enviando || !prompt.trim()}>
          <Bot size={13} /> {enviando ? '…' : 'Enviar'}
        </button>
      </div>
      <div className="dl-ia-proyecto-hint">Cmd/Ctrl + Enter para enviar</div>
    </div>
  );
}

function fmtMonto(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toLocaleString('es-CL')}`;
}

// Espejo liviano de TAXONOMIA_EQUIPAMIENTO (functions/src/lib/valueScoreEngine.ts)
// — solo key+label, para las casillas del simulador "qué pasaría si".
const ITEMS_SIMULADOR = [
  { key: 'frenado_autonomo', label: 'Frenado autónomo de emergencia' },
  { key: 'alerta_punto_ciego', label: 'Alerta de punto ciego' },
  { key: 'camara_360', label: 'Cámara 360°' },
  { key: 'control_crucero_adaptativo', label: 'Control crucero adaptativo' },
  { key: 'pantalla_tactil', label: 'Pantalla táctil' },
  { key: 'carplay_androidauto', label: 'CarPlay / Android Auto' },
  { key: 'clima_automatico', label: 'Climatizador automático' },
  { key: 'asientos_electricos', label: 'Asientos eléctricos' },
  { key: 'techo_panoramico', label: 'Techo panorámico' },
  { key: 'carga_inalambrica', label: 'Carga inalámbrica' },
  { key: 'llantas_aleacion', label: 'Llantas de aleación' },
];

const PESOS_DEFAULT = { pesoSeguridad: 35, pesoTecnologia: 25, pesoMotor: 25, pesoOtros: 15 };

function badgeValueScore(score) {
  if (score == null) return null;
  if (score >= 110) return { texto: 'Subvalorado', clase: 'cp-vs-badge--bueno' };
  if (score <= 90) return { texto: 'Sobrevalorado', clase: 'cp-vs-badge--malo' };
  return { texto: 'En línea', clase: 'cp-vs-badge--neutro' };
}

const FIN_FORM_VACIO = {
  entidadFinanciera: '', tasaInteresAnual: '', pieMinimoPct: '', plazoMeses: '',
  cuotaMensual: '', bonoPie: '', bonoDirectoPrecio: '', vigenciaDesde: '', vigenciaHasta: '',
  condiciones: '', fechaObservacion: new Date().toISOString().slice(0, 10),
};

// ── Lectura de mercado del comparador: la conclusión arriba, la grilla
// abajo. El PM necesita saber dónde quedó parado y qué hacer, no derivarlo
// de una tabla de ocho columnas. ──
function ResumenComparador({ resumen }) {
  if (!resumen) return null;

  if (!resumen.disponible) {
    return (
      <div className="cp-resumen cp-resumen--vacio">
        {resumen.motivo}
        {resumen.totalCompetidores > 0 && resumen.competidoresSinOferta > 0 && (
          <> · {resumen.competidoresSinOferta} de {resumen.totalCompetidores} competidores sin oferta cargada.</>
        )}
      </div>
    );
  }

  const lidera = resumen.posicionEfectivo === 1;

  return (
    <div className="cp-resumen">
      <div className="cp-resumen-kpis">
        <div className="cp-kpi">
          <span className="cp-kpi-label">Posición en precio efectivo</span>
          <span className={`cp-kpi-valor ${lidera ? 'cp-kpi-valor--bueno' : ''}`}>
            {resumen.posicionEfectivo}º<span className="cp-kpi-de">de {resumen.totalComparados}</span>
          </span>
          {resumen.posicionLista != null && resumen.posicionLista !== resumen.posicionEfectivo && (
            <span className="cp-kpi-sub">{resumen.posicionLista}º en precio de lista</span>
          )}
        </div>

        <div className="cp-kpi">
          <span className="cp-kpi-label">{lidera ? 'Ventaja sobre el 2º' : 'Bono para liderar'}</span>
          <span className="cp-kpi-valor">
            {lidera ? '—' : fmtMonto(resumen.bonoParaLiderar)}
          </span>
          {!lidera && resumen.brechaAlLiderPct != null && (
            <span className="cp-kpi-sub">{resumen.brechaAlLiderPct.toFixed(1)}% del precio</span>
          )}
        </div>

        <div className="cp-kpi">
          <span className="cp-kpi-label">Tu descuento</span>
          <span className="cp-kpi-valor">
            {resumen.descuentoPropioPct != null ? `${resumen.descuentoPropioPct.toFixed(1)}%` : '—'}
          </span>
          <span className="cp-kpi-sub">
            {resumen.descuentoPromedioCompetencia != null ?
              `competencia ${resumen.descuentoPromedioCompetencia.toFixed(1)}%` :
              'competencia sin datos'}
          </span>
        </div>

        <div className="cp-kpi">
          <span className="cp-kpi-label">Value Score</span>
          <span className="cp-kpi-valor">
            {resumen.valueScorePropio != null ? Math.round(resumen.valueScorePropio) : '—'}
          </span>
          <span className="cp-kpi-sub">
            {resumen.valueScorePromedioSet != null ?
              `set ${Math.round(resumen.valueScorePromedioSet)}` : 'set sin datos'}
          </span>
        </div>
      </div>

      {resumen.lecturas?.length > 0 && (
        <ul className="cp-lecturas">
          {resumen.lecturas.map((l, i) => (
            <li key={i} className={`cp-lectura cp-lectura--${l.tono}`}>{l.texto}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Tab Competencia: set de competidores + alertas + financiamiento por
// auto del proyecto. Acordeón — un auto expandido a la vez para no cargar
// todo de golpe (cada auto trae su propio comparador/ofertas/timeline). ──
function CompetenciaProyecto({ listaId, autos, competidores, alertConfig, pesosProyecto, onCambio }) {
  const [itemActivo, setItemActivo] = useState(null); // vehicleVersionId (number) expandido

  const [sugerencias, setSugerencias] = useState([]);
  const [sugerenciasLoading, setSugerenciasLoading] = useState(false);
  const [busquedaManual, setBusquedaManual] = useState('');
  const [resultadosManual, setResultadosManual] = useState([]);

  const [config, setConfig] = useState({ umbralPrecioPct: 2, alertarNuevasVersiones: true, alertarDescontinuados: true, alertarFinanciamiento: true });
  const [guardandoConfig, setGuardandoConfig] = useState(false);

  const [comparador, setComparador] = useState([]);
  const [comparadorResumen, setComparadorResumen] = useState(null);
  const [comparadorLoading, setComparadorLoading] = useState(false);

  const [ofertas, setOfertas] = useState([]);
  const [ofertasLoading, setOfertasLoading] = useState(false);
  const [finForm, setFinForm] = useState(FIN_FORM_VACIO);
  const [finGuardando, setFinGuardando] = useState(false);
  // A qué vehículo se le carga la oferta: el modelo propio o uno de sus
  // competidores. Sin esto solo se podían cargar bonos propios y el
  // comparador mostraba a la competencia siempre a precio de lista.
  const [finDestino, setFinDestino] = useState(null);

  const [alertasItem, setAlertasItem] = useState([]);

  const [valueScore, setValueScore] = useState(null);
  const [valueScoreLoading, setValueScoreLoading] = useState(false);
  const [pesosForm, setPesosForm] = useState(pesosProyecto || PESOS_DEFAULT);
  const [pesosGuardando, setPesosGuardando] = useState(false);
  const [simAjustePrecio, setSimAjustePrecio] = useState('');
  const [simItems, setSimItems] = useState([]);
  const [simResultado, setSimResultado] = useState(null);
  const [simCargando, setSimCargando] = useState(false);
  const simResultadoRef = useRef(null);

  // Diferencia Value Score simulado - actual. null si al backend le faltó
  // algún insumo (sin score previo o sin precio de referencia): en ese caso
  // mostramos el motivo en vez de un guión mudo.
  const simDelta = simResultado &&
    simResultado.valueScoreActual != null && simResultado.valueScoreSimulado != null ?
    simResultado.valueScoreSimulado - simResultado.valueScoreActual : null;

  const cargarPanelItem = async (vehicleVersionId) => {
    setConfig(alertConfig[vehicleVersionId] || { umbralPrecioPct: 2, alertarNuevasVersiones: true, alertarDescontinuados: true, alertarFinanciamiento: true });
    setSugerencias([]); setBusquedaManual(''); setResultadosManual([]); setFinForm(FIN_FORM_VACIO);
    setPesosForm(pesosProyecto || PESOS_DEFAULT);
    setSimAjustePrecio(''); setSimItems([]); setSimResultado(null);
    setFinDestino(vehicleVersionId);

    setComparadorLoading(true);
    setOfertasLoading(true);
    setValueScoreLoading(true);
    try {
      const [comp, ofertasResp, misAlertas, vsDetalle] = await Promise.all([
        obtenerComparadorFinanciero(listaId, vehicleVersionId),
        listarOfertasFinanciamiento(listaId, vehicleVersionId),
        listarMisAlertas({ limit: 100 }),
        obtenerValueScoreDetalle(vehicleVersionId),
      ]);
      setComparador(comp?.filas || []);
      setComparadorResumen(comp?.resumen || null);
      setOfertas(ofertasResp);
      setAlertasItem(misAlertas.filter((a) => a.vehicleVersionId === vehicleVersionId));
      setValueScore(vsDetalle?.score || null);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo cargar el detalle de competencia');
    } finally {
      setComparadorLoading(false);
      setOfertasLoading(false);
      setValueScoreLoading(false);
    }
  };

  const guardarPesos = async () => {
    setPesosGuardando(true);
    try {
      await guardarPesosProyecto(listaId, pesosForm);
      toast.success('Pesos guardados — aplican a todo el proyecto');
      await onCambio();
    } catch (e) {
      toast.error('No se pudieron guardar los pesos');
    } finally {
      setPesosGuardando(false);
    }
  };

  const toggleSimItem = (key) => {
    setSimItems((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const correrSimulacion = async (vehicleVersionId) => {
    // Sin ajuste de precio ni equipamiento marcado la simulación devuelve el
    // mismo escenario actual y parece que el botón no hiciera nada.
    const ajuste = Number(simAjustePrecio) || 0;
    if (ajuste === 0 && simItems.length === 0) {
      toast.info('Ingresa un ajuste de precio o marca equipamiento para simular.');
      return;
    }
    setSimCargando(true);
    try {
      const r = await simularValueScore(listaId, vehicleVersionId, {
        ajustePrecioPct: ajuste, itemsAAgregar: simItems,
      });
      setSimResultado(r);
      requestAnimationFrame(() => {
        simResultadoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    } catch (e) {
      toast.error(e?.message || 'No se pudo simular');
    } finally {
      setSimCargando(false);
    }
  };

  const toggleItem = (vehicleVersionId) => {
    if (itemActivo === vehicleVersionId) { setItemActivo(null); return; }
    setItemActivo(vehicleVersionId);
    cargarPanelItem(vehicleVersionId);
  };

  const buscarSugerencias = async (vehicleVersionId) => {
    setSugerenciasLoading(true);
    try {
      setSugerencias(await sugerirCompetidores(listaId, vehicleVersionId));
    } catch (e) {
      toast.error(e?.message || 'No se pudo sugerir competidores');
    } finally {
      setSugerenciasLoading(false);
    }
  };

  const buscarManual = async () => {
    if (!busquedaManual.trim()) return;
    const { autos: catalogo } = await listarCatalogo({ todos: true });
    const term = busquedaManual.toLowerCase();
    setResultadosManual(
      catalogo.filter((a) => `${a.marca} ${a.model} ${a.modelDetail}`.toLowerCase().includes(term)).slice(0, 10)
    );
  };

  const onAgregarCompetidor = async (vehicleVersionId, competidorVehicleVersionId, origen) => {
    try {
      await agregarCompetidor(listaId, vehicleVersionId, Number(competidorVehicleVersionId), origen);
      toast.success('Competidor agregado');
      setSugerencias((prev) => prev.filter((s) => s.id !== competidorVehicleVersionId));
      setResultadosManual([]); setBusquedaManual('');
      await onCambio();
      cargarPanelItem(vehicleVersionId);
    } catch (e) {
      toast.error(e?.message || 'No se pudo agregar el competidor');
    }
  };

  const onQuitarCompetidor = async (vehicleVersionId, competidorVehicleVersionId) => {
    try {
      await quitarCompetidor(listaId, vehicleVersionId, competidorVehicleVersionId);
      await onCambio();
      cargarPanelItem(vehicleVersionId);
    } catch (e) {
      toast.error('No se pudo quitar el competidor');
    }
  };

  const guardarConfig = async (vehicleVersionId) => {
    setGuardandoConfig(true);
    try {
      await guardarAlertaConfigProyecto(listaId, vehicleVersionId, config);
      toast.success('Preferencias de alerta guardadas');
      await onCambio();
    } catch (e) {
      toast.error('No se pudo guardar la configuración');
    } finally {
      setGuardandoConfig(false);
    }
  };

  const agregarFinanciamiento = async (vehicleVersionId) => {
    if (!finForm.fechaObservacion) { toast.warning('Falta la fecha de observación'); return; }
    // El destino puede ser el modelo propio o uno de sus competidores; el
    // panel que se recarga es siempre el del modelo propio abierto.
    const destino = finDestino ?? vehicleVersionId;
    setFinGuardando(true);
    try {
      await agregarOfertaFinanciamiento(listaId, destino, {
        entidadFinanciera: finForm.entidadFinanciera || undefined,
        tasaInteresAnual: finForm.tasaInteresAnual || undefined,
        pieMinimoPct: finForm.pieMinimoPct || undefined,
        plazoMeses: finForm.plazoMeses || undefined,
        cuotaMensual: finForm.cuotaMensual || undefined,
        bonoPie: finForm.bonoPie || undefined,
        bonoDirectoPrecio: finForm.bonoDirectoPrecio || undefined,
        vigenciaDesde: finForm.vigenciaDesde || undefined,
        vigenciaHasta: finForm.vigenciaHasta || undefined,
        condiciones: finForm.condiciones || undefined,
        fechaObservacion: finForm.fechaObservacion,
      });
      toast.success('Oferta registrada');
      setFinForm(FIN_FORM_VACIO);
      cargarPanelItem(vehicleVersionId);
    } catch (e) {
      toast.error('No se pudo registrar la oferta');
    } finally {
      setFinGuardando(false);
    }
  };

  const quitarFinanciamiento = async (ofertaId, vehicleVersionId) => {
    try {
      await eliminarOfertaFinanciamiento(ofertaId);
      cargarPanelItem(vehicleVersionId);
    } catch (e) {
      toast.error('No se pudo eliminar la oferta');
    }
  };

  return (
    <div className="cp-wrap">
      <div className="cp-header">
        <Radar size={16} />
        <div>
          <div className="cp-title">Inteligencia competitiva</div>
          <p className="cp-sub">
            Definí competidores por auto, umbrales de alerta y cargá lo que veas de financiamiento de la competencia —
            las alertas de precio/versión nueva/descontinuación se generan solas cada semana.
          </p>
        </div>
      </div>

      {autos.length === 0 ? (
        <div className="dl-ia-proyecto-vacio">Agrega autos al proyecto para poder definir su competencia.</div>
      ) : (
        <div className="cp-items">
          {autos.map((auto) => {
            const vvId = Number(auto.id);
            const activos = competidores[vvId] || [];
            const abierto = itemActivo === vvId;
            return (
              <div key={auto.id} className={`cp-item${abierto ? ' cp-item--abierto' : ''}`}>
                <button type="button" className="cp-item-head" onClick={() => toggleItem(vvId)}>
                  <div className="cp-item-nombre">{auto.marca} {auto.model} {auto.modelDetail}</div>
                  <div className="cp-item-count">{activos.length} competidor{activos.length !== 1 ? 'es' : ''}</div>
                </button>

                {!abierto && activos.length > 0 && (
                  <div className="cp-chips">
                    {activos.map((c) => (
                      <span key={c.vehicleVersionId} className="cp-chip">{c.marca} {c.model} {c.modelDetail}</span>
                    ))}
                  </div>
                )}

                {abierto && (
                  <div className="cp-panel">
                    {/* Competidores */}
                    <div className="cp-section">
                      <div className="cp-section-title">Competidores</div>
                      <div className="cp-chips">
                        {activos.length === 0 && <span className="cp-hint">Sin competidores definidos todavía.</span>}
                        {activos.map((c) => (
                          <span key={c.vehicleVersionId} className="cp-chip cp-chip--removible">
                            {c.marca} {c.model} {c.modelDetail}
                            {c.origen === 'automatico' && <Sparkles size={10} />}
                            <button type="button" onClick={() => onQuitarCompetidor(vvId, c.vehicleVersionId)} aria-label="Quitar competidor">
                              <X size={11} />
                            </button>
                          </span>
                        ))}
                      </div>

                      <div className="cp-add-row">
                        <button className="dl-btn" onClick={() => buscarSugerencias(vvId)} disabled={sugerenciasLoading}>
                          <Sparkles size={13} /> {sugerenciasLoading ? 'Buscando…' : 'Sugerir automáticamente'}
                        </button>
                        <input
                          type="text" className="dl-search-input" placeholder="Buscar modelo para agregar como competidor…"
                          value={busquedaManual} onChange={(e) => setBusquedaManual(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && buscarManual()}
                        />
                        <button className="dl-btn" onClick={buscarManual}><SearchIcon size={13} /></button>
                      </div>

                      {sugerencias.length > 0 && (
                        <div className="cp-resultados">
                          <div className="cp-resultados-label">Sugeridos (misma categoría/tipo de motor, precio ±15%)</div>
                          {sugerencias.map((s) => (
                            <button key={s.id} className="cp-resultado-item" onClick={() => onAgregarCompetidor(vvId, s.id, 'automatico')}>
                              <Plus size={12} /> {s.marca} {s.model} {s.modelDetail} · {fmtMonto(s.precio)}
                            </button>
                          ))}
                        </div>
                      )}
                      {resultadosManual.length > 0 && (
                        <div className="cp-resultados">
                          {resultadosManual.map((r) => (
                            <button key={r.id} className="cp-resultado-item" onClick={() => onAgregarCompetidor(vvId, Number(r.id), 'manual')}>
                              <Plus size={12} /> {r.marca} {r.model} {r.modelDetail}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Config de alertas */}
                    <div className="cp-section">
                      <div className="cp-section-title">Preferencias de alerta</div>
                      <div className="cp-config-row">
                        <label className="cp-config-field">
                          Umbral de precio
                          <input
                            type="number" min="0" step="0.5" className="dl-search-input cp-config-input"
                            value={config.umbralPrecioPct}
                            onChange={(e) => setConfig((p) => ({ ...p, umbralPrecioPct: e.target.value }))}
                          /> %
                        </label>
                        <label className="cp-config-check">
                          <input type="checkbox" checked={config.alertarNuevasVersiones}
                            onChange={(e) => setConfig((p) => ({ ...p, alertarNuevasVersiones: e.target.checked }))} />
                          Versión nueva
                        </label>
                        <label className="cp-config-check">
                          <input type="checkbox" checked={config.alertarDescontinuados}
                            onChange={(e) => setConfig((p) => ({ ...p, alertarDescontinuados: e.target.checked }))} />
                          Descontinuación
                        </label>
                        <label className="cp-config-check">
                          <input type="checkbox" checked={config.alertarFinanciamiento}
                            onChange={(e) => setConfig((p) => ({ ...p, alertarFinanciamiento: e.target.checked }))} />
                          Financiamiento
                        </label>
                        <button className="dl-btn dl-btn--primary" onClick={() => guardarConfig(vvId)} disabled={guardandoConfig}>
                          <Save size={12} /> Guardar
                        </button>
                      </div>
                    </div>

                    {/* Posicionamiento competitivo */}
                    <div className="cp-section">
                      <div className="cp-section-title">Posicionamiento competitivo</div>
                      {comparadorLoading ? (
                        <CarLoader label="Cargando comparador…" size={18} />
                      ) : comparador.length === 0 ? (
                        <span className="cp-hint">Sin datos de precio todavía.</span>
                      ) : (
                        <>
                          <ResumenComparador resumen={comparadorResumen} />

                          <div className="cp-table-wrap">
                            <table className="cp-table cp-table--comp">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Modelo</th>
                                  <th title="Precio de lista del catálogo">Lista</th>
                                  <th title="Bono al pie + bono directo cargados">Bono</th>
                                  <th title="Lista menos bonos">Efectivo</th>
                                  <th title="Diferencia de precio efectivo contra tu modelo">vs. tuyo</th>
                                  <th title="Cuota mensual cargada, o estimada desde tasa y plazo">Cuota</th>
                                  <th title="Pie más la suma de todas las cuotas">Costo total</th>
                                  <th title="Equipamiento vs precio, 100 = promedio del segmento">V. Score</th>
                                </tr>
                              </thead>
                              <tbody>
                                {comparador.map((f) => (
                                  <tr key={f.vehicleVersionId} className={f.esPropio ? 'cp-row-propio' : ''}>
                                    <td className="cp-td-pos">{f.posicionEfectivo ?? '—'}</td>
                                    <td>
                                      {f.vehiculo ? `${f.vehiculo.marca} ${f.vehiculo.model} ${f.vehiculo.modelDetail}` : '—'}
                                      {f.esPropio && <span className="cp-tag-propio">tuyo</span>}
                                      {f.sinOferta && !f.esPropio && <span className="cp-tag-sindato" title="Sin oferta cargada: se compara a precio de lista">sin oferta</span>}
                                    </td>
                                    <td className="cp-num">
                                      {fmtMonto(f.precioLista)}
                                      {f.tipoPrecio && <span className="cp-td-sub">{f.tipoPrecio}</span>}
                                    </td>
                                    <td className="cp-num">
                                      {f.descuentoTotal > 0 ? fmtMonto(f.descuentoTotal) : '—'}
                                      {f.descuentoPct > 0 && <span className="cp-td-sub">{f.descuentoPct.toFixed(1)}%</span>}
                                    </td>
                                    <td className="cp-num"><strong>{fmtMonto(f.precioEfectivo)}</strong></td>
                                    <td className="cp-num">
                                      {f.esPropio ? '—' : (
                                        f.gapVsPropio == null ? '—' : (
                                          <span className={f.gapVsPropio > 0 ? 'cp-gap-caro' : 'cp-gap-barato'}>
                                            {f.gapVsPropio > 0 ? '+' : '−'}{fmtMonto(Math.abs(f.gapVsPropio))}
                                            <span className="cp-td-sub">{f.gapVsPropioPct > 0 ? '+' : ''}{f.gapVsPropioPct?.toFixed(1)}%</span>
                                          </span>
                                        )
                                      )}
                                    </td>
                                    <td className="cp-num">
                                      {f.cuotaMensual != null ? fmtMonto(f.cuotaMensual) : '—'}
                                      {f.cuotaEsEstimada && <span className="cp-td-sub" title="Calculada con sistema francés a partir de tasa, pie y plazo">estimada</span>}
                                    </td>
                                    <td className="cp-num">
                                      {f.costoTotalCredito != null ? fmtMonto(f.costoTotalCredito) : '—'}
                                      {f.sobrecostoFinanciero > 0 && <span className="cp-td-sub">+{fmtMonto(f.sobrecostoFinanciero)} financiero</span>}
                                    </td>
                                    <td className="cp-num">{f.valueScore != null ? Math.round(f.valueScore) : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Value Score — equipamiento vs precio */}
                    <div className="cp-section">
                      <div className="cp-section-title"><Gauge size={13} style={{ verticalAlign: -2 }} /> Value Score</div>
                      {valueScoreLoading ? (
                        <CarLoader label="Cargando Value Score…" size={18} />
                      ) : !valueScore ? (
                        <span className="cp-hint">Sin Value Score calculado todavía para este modelo (falta categoría curada o ficha técnica).</span>
                      ) : (
                        <div className="cp-vs-row">
                          <div className="cp-vs-numero">{valueScore.valueScore != null ? Math.round(valueScore.valueScore) : '—'}</div>
                          <div>
                            {badgeValueScore(valueScore.valueScore) && (
                              <span className={`cp-vs-badge ${badgeValueScore(valueScore.valueScore).clase}`}>
                                {badgeValueScore(valueScore.valueScore).texto}
                              </span>
                            )}
                            <div className="cp-hint" style={{ marginTop: 4 }}>
                              Score equipamiento: {valueScore.scoreEquipamiento != null ? Math.round(valueScore.scoreEquipamiento) : '—'}/100 ·
                              ficha {valueScore.fuenteSpecs === 'exacta' ? 'exacta' : 'aproximada (mismo modelo)'} · 100 = promedio del segmento
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="cp-vs-pesos">
                        <div className="cp-vs-subtitle">Pesos de categoría para este proyecto</div>
                        {['pesoSeguridad', 'pesoTecnologia', 'pesoMotor', 'pesoOtros'].map((k) => (
                          <label key={k} className="cp-vs-peso-field">
                            {{ pesoSeguridad: 'Seguridad', pesoTecnologia: 'Tecnología', pesoMotor: 'Motor', pesoOtros: 'Otros' }[k]}
                            <input
                              type="number" min="0" max="100" className="dl-search-input cp-config-input"
                              value={pesosForm[k]}
                              onChange={(e) => setPesosForm((p) => ({ ...p, [k]: e.target.value }))}
                            />%
                          </label>
                        ))}
                        <button className="dl-btn" onClick={guardarPesos} disabled={pesosGuardando}>
                          <Save size={12} /> {pesosGuardando ? 'Guardando…' : 'Guardar pesos'}
                        </button>
                      </div>

                      <div className="cp-vs-sim">
                        <div className="cp-vs-subtitle">Simulador — ¿qué pasaría si…?</div>
                        <div className="cp-vs-sim-row">
                          <label>
                            Ajuste de precio
                            <input
                              type="number" className="dl-search-input cp-config-input" placeholder="ej: -5"
                              value={simAjustePrecio} onChange={(e) => setSimAjustePrecio(e.target.value)}
                            />%
                          </label>
                          <button className="dl-btn dl-btn--primary" onClick={() => correrSimulacion(vvId)} disabled={simCargando}>
                            {simCargando ? 'Simulando…' : 'Simular'}
                          </button>
                        </div>
                        <div className="cp-vs-sim-items">
                          {ITEMS_SIMULADOR.map((item) => (
                            <label key={item.key} className="cp-vs-sim-item">
                              <input type="checkbox" checked={simItems.includes(item.key)} onChange={() => toggleSimItem(item.key)} />
                              {item.label}
                            </label>
                          ))}
                        </div>
                        {simResultado && (
                          <div className="cp-vs-sim-resultado" ref={simResultadoRef}>
                            {simDelta == null ? (
                              <div className="cp-vs-sim-vacio">
                                No se puede simular este modelo todavía: falta su Value Score
                                calculado o un precio de referencia vigente.
                              </div>
                            ) : (
                              <>
                                <div className="cp-vs-sim-cifras">
                                  <div className="cp-vs-sim-cifra">
                                    <span className="cp-vs-sim-cifra-label">Value Score actual</span>
                                    <span className="cp-vs-sim-cifra-valor">{Math.round(simResultado.valueScoreActual)}</span>
                                  </div>
                                  <ArrowRight size={16} className="cp-vs-sim-flecha" />
                                  <div className="cp-vs-sim-cifra">
                                    <span className="cp-vs-sim-cifra-label">Simulado</span>
                                    <span className="cp-vs-sim-cifra-valor">{Math.round(simResultado.valueScoreSimulado)}</span>
                                  </div>
                                  <span className={`cp-vs-sim-delta ${simDelta >= 0 ? 'cp-vs-sim-delta--sube' : 'cp-vs-sim-delta--baja'}`}>
                                    {simDelta >= 0 ? '+' : ''}{simDelta.toFixed(1)} pts
                                  </span>
                                </div>
                                <div className="cp-vs-sim-detalle">
                                  {simResultado.precioSimulado != null && <>Precio simulado: <strong>{fmtMonto(simResultado.precioSimulado)}</strong></>}
                                  {simItems.length > 0 && <> · {simItems.length} item{simItems.length !== 1 ? 's' : ''} de equipamiento agregado{simItems.length !== 1 ? 's' : ''}</>}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Financiamiento manual */}
                    <div className="cp-section">
                      <div className="cp-section-title"><Landmark size={13} style={{ verticalAlign: -2 }} /> Ofertas de financiamiento (carga manual)</div>
                      <p className="cp-hint cp-hint--bloque">
                        Carga lo que observes en terreno: bonos, tasa, pie y plazo. Los bonos alimentan
                        el precio efectivo; la tasa, el pie y el plazo alimentan la cuota y el costo
                        total del crédito. Puedes cargar ofertas de tus competidores eligiéndolos en el
                        selector; el historial de abajo muestra las de todo el set.
                      </p>
                      {ofertasLoading ? (
                        <CarLoader label="Cargando ofertas…" size={18} />
                      ) : ofertas.length > 0 && (
                        <div className="cp-table-wrap">
                          <table className="cp-table">
                            <thead>
                              <tr><th>Vehículo</th><th>Entidad</th><th>Tasa</th><th>Pie</th><th>Plazo</th><th>Cuota</th><th>Bono pie</th><th>Bono directo</th><th>Fecha</th><th /></tr>
                            </thead>
                            <tbody>
                              {ofertas.map((o) => (
                                <tr key={o.id} className={o.esPropio ? 'cp-row-propio' : ''}>
                                  <td>
                                    {o.vehiculo ? `${o.vehiculo.marca} ${o.vehiculo.model} ${o.vehiculo.modelDetail}` : '—'}
                                    {o.esPropio && <span className="cp-tag-propio">tuyo</span>}
                                  </td>
                                  <td>{o.entidadFinanciera || '—'}</td>
                                  <td>{o.tasaInteresAnual != null ? `${o.tasaInteresAnual}%` : '—'}</td>
                                  <td>{o.pieMinimoPct != null ? `${o.pieMinimoPct}%` : '—'}</td>
                                  <td>{o.plazoMeses || '—'}</td>
                                  <td>{o.cuotaMensual != null ? fmtMonto(o.cuotaMensual) : '—'}</td>
                                  <td>{o.bonoPie != null ? fmtMonto(o.bonoPie) : '—'}</td>
                                  <td>{o.bonoDirectoPrecio != null ? fmtMonto(o.bonoDirectoPrecio) : '—'}</td>
                                  <td>{o.fechaObservacion}</td>
                                  <td><button className="dl-btn-quitar" onClick={() => quitarFinanciamiento(o.id, vvId)}><Trash2 size={11} /></button></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div className="cp-fin-form">
                        <select
                          className="dl-search-input cp-fin-destino"
                          value={finDestino ?? vvId}
                          onChange={(e) => setFinDestino(Number(e.target.value))}
                          title="A qué vehículo corresponde esta oferta"
                        >
                          <option value={vvId}>
                            {auto.marca} {auto.model} {auto.modelDetail} (tuyo)
                          </option>
                          {activos.map((c) => (
                            <option key={c.vehicleVersionId} value={c.vehicleVersionId}>
                              {c.marca} {c.model} {c.modelDetail}
                            </option>
                          ))}
                        </select>
                        <input className="dl-search-input" placeholder="Entidad financiera" value={finForm.entidadFinanciera} onChange={(e) => setFinForm((p) => ({ ...p, entidadFinanciera: e.target.value }))} />
                        <input className="dl-search-input" type="number" placeholder="Tasa % anual" value={finForm.tasaInteresAnual} onChange={(e) => setFinForm((p) => ({ ...p, tasaInteresAnual: e.target.value }))} />
                        <input className="dl-search-input" type="number" placeholder="Pie mínimo %" value={finForm.pieMinimoPct} onChange={(e) => setFinForm((p) => ({ ...p, pieMinimoPct: e.target.value }))} />
                        <input className="dl-search-input" type="number" placeholder="Plazo (meses)" value={finForm.plazoMeses} onChange={(e) => setFinForm((p) => ({ ...p, plazoMeses: e.target.value }))} />
                        <input className="dl-search-input" type="number" placeholder="Cuota mensual" value={finForm.cuotaMensual} onChange={(e) => setFinForm((p) => ({ ...p, cuotaMensual: e.target.value }))} />
                        <input className="dl-search-input" type="number" placeholder="Bono pie $" value={finForm.bonoPie} onChange={(e) => setFinForm((p) => ({ ...p, bonoPie: e.target.value }))} />
                        <input className="dl-search-input" type="number" placeholder="Bono directo $" value={finForm.bonoDirectoPrecio} onChange={(e) => setFinForm((p) => ({ ...p, bonoDirectoPrecio: e.target.value }))} />
                        <input className="dl-search-input" type="date" title="Vigencia desde" value={finForm.vigenciaDesde} onChange={(e) => setFinForm((p) => ({ ...p, vigenciaDesde: e.target.value }))} />
                        <input className="dl-search-input" type="date" title="Vigencia hasta" value={finForm.vigenciaHasta} onChange={(e) => setFinForm((p) => ({ ...p, vigenciaHasta: e.target.value }))} />
                        <input className="dl-search-input" type="date" title="Fecha en que lo observaste" value={finForm.fechaObservacion} onChange={(e) => setFinForm((p) => ({ ...p, fechaObservacion: e.target.value }))} />
                        <input className="dl-search-input cp-fin-condiciones" placeholder="Condiciones (opcional)" value={finForm.condiciones} onChange={(e) => setFinForm((p) => ({ ...p, condiciones: e.target.value }))} />
                        <button className="dl-btn dl-btn--primary" onClick={() => agregarFinanciamiento(vvId)} disabled={finGuardando}>
                          <Plus size={12} /> {finGuardando ? 'Guardando…' : 'Agregar oferta'}
                        </button>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="cp-section">
                      <div className="cp-section-title">Timeline de alertas de este modelo</div>
                      {alertasItem.length === 0 ? (
                        <span className="cp-hint">Sin alertas registradas todavía para este modelo.</span>
                      ) : (
                        <div className="cp-timeline">
                          {alertasItem.map((a) => (
                            <div key={a.id} className="cp-timeline-item">
                              <span className="cp-timeline-fecha">{new Date(a.createdAt).toLocaleDateString('es-CL')}</span>
                              <span>{a.tipoEvento}{a.competidor ? ` · ${a.competidor.marca} ${a.competidor.model} ${a.competidor.modelDetail}` : ''} — {a.valorAnterior && a.valorNuevo ? `${a.valorAnterior} → ${a.valorNuevo}` : (a.valorNuevo || a.valorAnterior)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DetalleLista() {
  const { id } = useParams();
  const [lista, setLista] = useState(null);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [autos, setAutos] = useState([]);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevaDescripcion, setNuevaDescripcion] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [tablaVisible, setTablaVisible] = useState(true);
  const [vista, setVista] = useState('proyecto'); // 'proyecto' | 'comparar'
  const [autosComparar, setAutosComparar] = useState([]);
  const [tipoComparado, setTipoComparado] = useState('Todos');
  const [datosLinea, setDatosLinea] = useState([]);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState("");
  const [iaResultado, setIaResultado] = useState(null);

  // ── Seguimiento de precios: bitácora manual privada del proyecto,
  // separada del historial automático — nunca toca los datos generales. ──
  const [seguimiento, setSeguimiento] = useState({}); // { [vehicleVersionId]: [{id,tipoPrecio,precio,fecha,nota}] }
  const [seguimientoAuto, setSeguimientoAuto] = useState(null);
  const [segTipo, setSegTipo] = useState('');
  const [segPrecio, setSegPrecio] = useState('');
  const [segFecha, setSegFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [segNota, setSegNota] = useState('');
  const [segGuardando, setSegGuardando] = useState(false);

  // ── Subcategoría manual: la llena el PM para ESTE proyecto, no toca la
  // categoría global del catálogo. ──
  const [subcategorias, setSubcategorias] = useState({}); // { [vehicleVersionId]: texto }
  const [editandoSubcatId, setEditandoSubcatId] = useState(null);
  const [subcatInput, setSubcatInput] = useState('');

  // ── Inteligencia competitiva: competidores + config de alerta por ítem,
  // vienen ya armados desde obtenerLista(). ──
  const [competidores, setCompetidores] = useState({}); // { [vehicleVersionId]: [{vehicleVersionId,marca,model,modelDetail,origen}] }
  const [alertConfig, setAlertConfig] = useState({}); // { [vehicleVersionId]: {umbralPrecioPct,...} }
  const [pesosProyecto, setPesosProyecto] = useState(null); // {pesoSeguridad,pesoTecnologia,pesoMotor,pesoOtros} | null (null = usa el default global)

  // ── Vista tipo catálogo: mismas columnas/orden que muestra CatalogoTabla ──
  const [sortField, setSortField] = useState('marca');
  const [sortDir, setSortDir] = useState('asc');
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortArrow = (field) => (sortField !== field ? '' : (sortDir === 'asc' ? ' ▲' : ' ▼'));

  const getPrecioPorTipo = (auto, tipo) => {
    const idx = (auto.tiposprecio || []).findIndex(t => String(t).toLowerCase() === tipo.toLowerCase());
    if (idx < 0) return null;
    const n = Number(String(auto.precio?.[idx] ?? '').replace(/\D+/g, ''));
    return n > 0 ? n : null;
  };

  // ── IA helpers ────────────────────────────────────────────────────────────

  function compactSerieForLLM(serie, maxFechas = 24) {
    if (!Array.isArray(serie)) return [];
    return serie.slice(-maxFechas);
  }

  function buildSerieText(serie) {
    return (serie || []).map(row => {
      const { fecha, ...rest } = row;
      const parts = Object.entries(rest)
        .filter(([, v]) => v !== null && v !== undefined && v !== 0)
        .map(([k, v]) => `${k}: ${Number(v).toLocaleString('es-CL')}`)
        .join(" | ");
      return `${fecha} -> ${parts}`;
    }).join("\n");
  }

  async function analizarConIA() {
    if (iaLoading || autosComparar.length < 2) return;
    setIaLoading(true);
    setIaError("");
    setIaResultado(null);
    try {
      const seleccionados = autos.filter(a => autosComparar.includes(a.id));
      const nombresModelos = seleccionados.map(a => `${a.marca} ${a.model} ${a.modelDetail}`).join(" vs ");
      const serieTexto = buildSerieText(compactSerieForLLM(datosLinea, 24));

      const res = await consultarClaudePM({
        uid,
        prompt: `Análisis competitivo PM: ${nombresModelos}\n\nTipo de precio: ${tipoComparado}\n\nSERIE HISTÓRICA:\n${serieTexto || "Sin datos."}\n\n1. Posicionamiento precio/specs\n2. Mejor propuesta de valor\n3. Gaps detectados\n4. Recomendaciones de pricing`.trim(),
        historial: [],
      });
      setIaResultado(res.data);
    } catch (e) {
      console.error("Error ClaudePM:", e);
      setIaError(e?.message || "Error al generar análisis con IA");
    } finally {
      setIaLoading(false);
    }
  }

  // ── Selección ─────────────────────────────────────────────────────────────

  const toggleSeleccionarTodos = () => {
    setAutosComparar(autosComparar.length === autos.length ? [] : autos.map(a => a.id));
  };

  const toggleCompararAuto = (autoId) => {
    setAutosComparar(prev => prev.includes(autoId) ? prev.filter(x => x !== autoId) : [...prev, autoId]);
  };

  const autosSeleccionados = autos.filter(auto => autosComparar.includes(auto.id));
  const serieNombre = (a) => `${a.marca} ${a.model} ${a.modelDetail}`;

  // ── Carga de datos ────────────────────────────────────────────────────────

  const cargarLista = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setCargandoLista(true);
    try {
      const {
        lista: data, autos: identidades, seguimiento: seg, subcategorias: subcats,
        competidores: comp, alertConfig: alertCfg, pesosProyecto: pesosResp,
      } = await obtenerLista(id);
      setLista(data);
      setNuevoNombre(data.nombre);
      setNuevaDescripcion(data.descripcion || '');
      setSeguimiento(seg || {});
      setSubcategorias(subcats || {});
      setCompetidores(comp || {});
      setAlertConfig(alertCfg || {});
      setPesosProyecto(pesosResp || null);

      if (identidades?.length > 0) {
        // Resolver cada identidad al snapshot de precio más reciente en toda
        // la historia — reemplaza el patrón de "doc más reciente en Firestore".
        const resueltos = await Promise.all(identidades.map(async (m) => {
          const actual = await resolverModeloActual(m.marca, m.model, m.modelDetail);
          return actual || { ...m, id: `${m.marca}_${m.model}_${m.modelDetail}` };
        }));
        setAutos(resueltos);
      } else {
        setAutos([]);
      }
    } catch (e) {
      console.error(e);
      toast.error('No se pudo cargar la lista');
    } finally {
      setCargandoLista(false);
    }
  };

  const PRICE_TYPES = useMemo(() => {
    const freq = new Map();
    for (const a of autos) {
      for (const t of (a.tiposprecio || [])) {
        const k = String(t || '').trim();
        if (k) freq.set(k, (freq.get(k) || 0) + 1);
      }
    }
    return Array.from(freq.entries()).sort((a, b) => b[1] - a[1]).map(([k]) => k).slice(0, 4);
  }, [autos]);

  const autosOrdenados = useMemo(() => {
    const list = [...autos];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortField.startsWith('precio:')) {
        const t = sortField.slice(7);
        const va = getPrecioPorTipo(a, t) ?? -1, vb = getPrecioPorTipo(b, t) ?? -1;
        return (va - vb) * dir;
      }
      if (sortField === 'date_add') return ((a.date_add || 0) - (b.date_add || 0)) * dir;
      return String(a[sortField] ?? '').toLowerCase().localeCompare(String(b[sortField] ?? '').toLowerCase()) * dir;
    });
    return list;
  }, [autos, sortField, sortDir]);

  const resumenUltimoPrecio = autosSeleccionados.map(auto => {
    const nombre = serieNombre(auto);
    const hist = datosLinea.filter(item => item[nombre] !== undefined);
    const ultimo = hist[hist.length - 1];
    // Cada fila de datosLinea guarda el precio bajo una clave dinámica
    // (el nombre del modelo), no bajo "precio" — de ahí que siempre
    // mostraba "—" aunque sí había datos.
    return { id: auto.id, marca: auto.marca, model: auto.model, modelDetail: auto.modelDetail, precio: ultimo ? ultimo[nombre] : '—', fecha: ultimo?.fecha ?? '—' };
  });

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const buscarAutos = async () => {
    if (!busqueda.trim()) return;
    const { autos: catalogo } = await listarCatalogo({ todos: true });
    const coincidencias = catalogo
      .filter(a => `${a.marca} ${a.model} ${a.modelDetail}`.toLowerCase().includes(busqueda.toLowerCase()));
    setResultadosBusqueda(coincidencias.slice(0, 10));
  };

  const agregarAutoALaLista = async (auto) => {
    const user = auth.currentUser;
    if (!user || !lista) return;
    try {
      await agregarAutoAListaApi(id, auto.marca, auto.model, auto.modelDetail);
      toast.success('Auto agregado a la lista');
      setBusqueda('');
      setResultadosBusqueda([]);
      cargarLista();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo agregar el auto');
    }
  };

  const guardarCambiosModal = async () => {
    const user = auth.currentUser;
    if (!user) return;
    await actualizarListaApi(id, nuevoNombre.trim(), nuevaDescripcion.trim());
    toast.success('Cambios guardados');
    setModalVisible(false);
    cargarLista();
  };

  const quitarAutoDeLista = async (auto) => {
    const user = auth.currentUser;
    if (!user || !lista) return;
    try {
      await quitarAutoDeListaApi(id, auto.marca, auto.model, auto.modelDetail);
      toast.success('Auto eliminado');
      cargarLista();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo quitar el auto');
    }
  };

  // ── Seguimiento de precios (bitácora manual, privada del proyecto) ──

  const abrirSeguimiento = (auto) => {
    setSeguimientoAuto(auto);
    setSegTipo((auto.tiposprecio || [])[0] || '');
    setSegPrecio('');
    setSegFecha(new Date().toISOString().slice(0, 10));
    setSegNota('');
  };

  const cerrarSeguimiento = () => setSeguimientoAuto(null);

  const agregarEntradaSeguimiento = async () => {
    if (!seguimientoAuto) return;
    const precioNum = Number(String(segPrecio).replace(/\D/g, ''));
    if (!segTipo.trim() || !precioNum || !segFecha) {
      toast.error('Completa tipo, precio y fecha.');
      return;
    }
    setSegGuardando(true);
    try {
      const entrada = await agregarSeguimientoPrecioApi(
        id, Number(seguimientoAuto.id), segTipo.trim(), precioNum, segFecha, segNota.trim()
      );
      setSeguimiento(prev => {
        const next = { ...prev };
        const lista = [...(next[seguimientoAuto.id] || []), entrada].sort((a, b) => a.fecha.localeCompare(b.fecha));
        next[seguimientoAuto.id] = lista;
        return next;
      });
      setSegPrecio('');
      setSegNota('');
      toast.success('Registro agregado');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo agregar el registro');
    } finally {
      setSegGuardando(false);
    }
  };

  const eliminarEntradaSeguimiento = async (entradaId) => {
    try {
      await eliminarSeguimientoPrecioApi(entradaId);
      setSeguimiento(prev => {
        const next = { ...prev };
        for (const key of Object.keys(next)) next[key] = next[key].filter(e => e.id !== entradaId);
        return next;
      });
    } catch (e) {
      console.error(e);
      toast.error('No se pudo eliminar el registro');
    }
  };

  const ultimoManual = (auto) => {
    const entradas = seguimiento[auto.id];
    return entradas?.length ? entradas[entradas.length - 1] : null;
  };

  // ── Subcategoría manual (privada del proyecto) ──

  const sugerenciasSubcategoria = useMemo(
    () => [...new Set(Object.values(subcategorias))].filter(Boolean).sort(),
    [subcategorias]
  );

  const iniciarEdicionSubcat = (auto) => {
    setEditandoSubcatId(auto.id);
    setSubcatInput(subcategorias[auto.id] || '');
  };

  const guardarSubcategoria = async (autoId) => {
    const texto = subcatInput.trim();
    setEditandoSubcatId(null);
    try {
      await guardarSubcategoriaProyectoApi(id, Number(autoId), texto);
      setSubcategorias(prev => {
        const next = { ...prev };
        if (texto) next[autoId] = texto; else delete next[autoId];
        return next;
      });
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar la subcategoría');
    }
  };

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => { cargarLista(); }, [id]);

  useEffect(() => {
    const limpiarPrecio = v => typeof v === 'string' ? (Number(v.replace(/\D+/g, '')) || 0) : v;
    const parseCLDate = s => { const [dd, mm, yyyy] = String(s).split('-').map(Number); return new Date(yyyy, (mm || 1) - 1, dd || 1); };

    const cargarHistorial = async () => {
      if (autosComparar.length === 0) return;
      let historial = [];
      for (const auto of autos.filter(a => autosComparar.includes(a.id))) {
        const { historial: snapshots } = await obtenerHistorialPrecios(auto.marca, auto.model, auto.modelDetail);
        snapshots.forEach(data => {
          const fecha = new Date(data.date_add * 1000).toLocaleDateString('es-CL');
          const nombre = `${auto.marca} ${auto.model} ${auto.modelDetail}`;
          if (tipoComparado === 'Todos') {
            const val = data.precio?.[0];
            if (val) historial.push({ nombre, fecha, precio: limpiarPrecio(val) });
          } else {
            const i = data.tiposprecio?.indexOf(tipoComparado);
            if (i >= 0) { const val = data.precio?.[i]; if (val) historial.push({ nombre, fecha, precio: limpiarPrecio(val) }); }
          }
        });
      }
      const fechasUnicas = [...new Set(historial.map(h => h.fecha))].sort((a, b) => parseCLDate(a) - parseCLDate(b));
      setDatosLinea(fechasUnicas.map(fecha => {
        const fila = { fecha };
        historial.filter(h => h.fecha === fecha).forEach(h => { fila[h.nombre] = h.precio; });
        return fila;
      }));
    };
    cargarHistorial();
  }, [autosComparar, tipoComparado, autos]);

  // ── Export ────────────────────────────────────────────────────────────────

  const exportarExcel = (soloSeleccionados = false) => {
    const fuente = soloSeleccionados ? autosSeleccionados : autos;
    if (!fuente?.length) { toast.info(soloSeleccionados ? 'No hay autos seleccionados.' : 'No hay autos en la lista.'); return; }
    const tipos = Array.from(new Set(fuente.flatMap(a => a.tiposprecio || []))).slice(0, 10);
    const rows = fuente.map(a => {
      const fila = { ID: a.id, Marca: a.marca || '', Modelo: a.model || '', Version: a.modelDetail || '', Categoria: a.categoria || '' };
      tipos.forEach(t => {
        const idx = (a.tiposprecio || []).indexOf(t);
        const val = idx >= 0 ? a.precio?.[idx] : '';
        fila[`Precio (${t})`] = typeof val === 'string' ? Number(val.replace(/\D+/g, '')) || '' : val ?? '';
      });
      return fila;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Listado');
    XLSX.writeFile(wb, `lista_${lista?.nombre || id}_${soloSeleccionados ? 'seleccion' : 'todos'}.xlsx`);
  };

  // Reporte completo: los mismos datos que se ven en catálogo para cada
  // auto del proyecto, más una hoja aparte con toda la bitácora de
  // seguimiento manual (privada, no se mezcla con la hoja de catálogo).
  const exportarReporte = () => {
    if (!autos.length) { toast.info('No hay autos en el proyecto.'); return; }
    const tiposTodos = Array.from(new Set(autos.flatMap(a => a.tiposprecio || [])));

    const filasCatalogo = autos.map(a => {
      const manual = ultimoManual(a);
      const fila = {
        Marca: a.marca || '', Modelo: a.model || '', Versión: a.modelDetail || '',
        Categoría: a.categoria || '', 'Tipo motor': a.tipomotor || '', Subcategoría: subcategorias[a.id] || '',
        Origen: a.origen || '',
        Fecha: a.date_add ? new Date(a.date_add * 1000).toLocaleDateString('es-CL') : '',
      };
      tiposTodos.forEach(t => { fila[`Precio (${t})`] = getPrecioPorTipo(a, t) ?? ''; });
      fila['Último precio manual'] = manual ? manual.precio : '';
      fila['Tipo (manual)'] = manual ? manual.tipoPrecio : '';
      fila['Fecha (manual)'] = manual ? new Date(`${manual.fecha}T00:00:00`).toLocaleDateString('es-CL') : '';
      return fila;
    });

    const filasSeguimiento = autos.flatMap(a =>
      (seguimiento[a.id] || []).map(e => ({
        Marca: a.marca || '', Modelo: a.model || '', Versión: a.modelDetail || '',
        Fecha: new Date(`${e.fecha}T00:00:00`).toLocaleDateString('es-CL'),
        Tipo: e.tipoPrecio, Precio: e.precio, Nota: e.nota || '',
      }))
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasCatalogo), 'Catálogo');
    if (filasSeguimiento.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasSeguimiento), 'Seguimiento');
    }
    XLSX.writeFile(wb, `reporte_${lista?.nombre || id}.xlsx`);
  };

  const clpFmt = (v) => Number.isFinite(Number(v))
    ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(Number(v))
    : v;

  const iaListo = autosComparar.length >= 2;
  const iaFaltante = Math.max(0, 2 - autosComparar.length);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="detalle-lista-main">
      <div className="detalle-lista-wrapper dl-wrapper--sin-padding-abajo">

        {/* Header */}
        <div className="dl-page-header">
          <div className="dl-page-title">
            <div className="dl-page-icon"><Folder size={16} /></div>
            <h1 className="dl-page-heading">{lista?.nombre || id}</h1>
          </div>
          <div className="dl-header-actions">
            <button
              className="dl-btn"
              onClick={() => { setNuevoNombre(lista?.nombre || ''); setNuevaDescripcion(lista?.descripcion || ''); setModalVisible(true); }}
            >
              <Pencil size={13} /> Editar
            </button>
            <button className="dl-btn" onClick={() => exportarExcel(true)}>
              <Download size={13} /> Selección
            </button>
            <button className="dl-btn" onClick={() => exportarExcel(false)}>
              <Download size={13} /> Exportar todo
            </button>
            <button className="dl-btn dl-btn--primary" onClick={exportarReporte} title="Excel con todos los datos de catálogo + tu bitácora de seguimiento">
              <FileSpreadsheet size={13} /> Reporte completo
            </button>
          </div>
        </div>

        {/* Tabs de vista */}
        <div className="dl-view-tabs">
          <button className={`dl-view-tab ${vista === 'proyecto' ? 'active' : ''}`} onClick={() => setVista('proyecto')}>
            <Folder size={13} /> Proyecto
          </button>
          <button className={`dl-view-tab ${vista === 'comparar' ? 'active' : ''}`} onClick={() => setVista('comparar')}>
            <BarChart3 size={13} /> Comparar
          </button>
          <button className={`dl-view-tab ${vista === 'ia' ? 'active' : ''}`} onClick={() => setVista('ia')}>
            <Bot size={13} /> IA
          </button>
          <button className={`dl-view-tab ${vista === 'competencia' ? 'active' : ''}`} onClick={() => setVista('competencia')}>
            <Radar size={13} /> Competencia
          </button>
        </div>

      </div>

      {vista === 'comparar' ? (
        <ComparadorModelos embedded initialModels={autos} />
      ) : vista === 'ia' ? (
        <div className="detalle-lista-wrapper dl-wrapper--sin-padding-arriba">
          <ProyectoIA listaId={id} />
        </div>
      ) : vista === 'competencia' ? (
        <div className="detalle-lista-wrapper dl-wrapper--sin-padding-arriba">
          <CompetenciaProyecto listaId={id} autos={autos} competidores={competidores} alertConfig={alertConfig} pesosProyecto={pesosProyecto} onCambio={cargarLista} />
        </div>
      ) : (
      <div className="detalle-lista-wrapper dl-wrapper--sin-padding-arriba">
        {/* Búsqueda */}
        <div className="dl-search-card">
          <div className="dl-search-label">
            <SearchIcon size={13} /> Agregar modelos a la lista
          </div>
          <div className="dl-search-row">
            <input
              type="text"
              placeholder="Buscar marca, modelo o versión…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscarAutos()}
              className="dl-search-input"
            />
            <button className="dl-btn dl-btn--primary" onClick={buscarAutos}>
              <SearchIcon size={13} /> Buscar
            </button>
          </div>
        </div>

        {/* Resultados búsqueda */}
        {resultadosBusqueda.length > 0 && (
          <div className="dl-results-card">
            <div className="dl-results-header">{resultadosBusqueda.length} resultado{resultadosBusqueda.length !== 1 ? 's' : ''}</div>
            <ul className="dl-results-ul">
              {resultadosBusqueda.map(auto => (
                <li key={auto.id} className="dl-result-item">
                  <div className="dl-result-info">
                    <div className="dl-result-name">{auto.marca} {auto.model} {auto.modelDetail}</div>
                    <div className="dl-result-chips">
                      {auto.categoria && <span className="badge-soft"><Tag size={10} /> {auto.categoria}</span>}
                      {(auto.tiposprecio || []).slice(0, 4).map((t, i) => (
                        <span key={`${auto.id}-tp-${i}`} className="badge-pill">{t}</span>
                      ))}
                    </div>
                  </div>
                  <button className="dl-btn-agregar" onClick={() => agregarAutoALaLista(auto)}>
                    <Plus size={13} /> Agregar
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* IA bar */}
        <div className="dl-ia-bar">
          <div className="ai-tooltip-wrapper">
            <button
              type="button"
              className={["ai-fab", iaLoading ? "is-loading" : "", iaListo && !iaLoading ? "is-ready" : ""].filter(Boolean).join(" ")}
              onClick={analizarConIA}
              disabled={iaLoading || !iaListo}
            >
              <span className="ai-fab-icon">{iaLoading ? "⏳" : "💡"}</span>
            </button>
            <div className="ai-tooltip">
              <div className="ai-tooltip-title">Análisis estratégico PM</div>
              <div className="ai-tooltip-text">Comparativo precio/specs, gaps de mercado y recomendaciones de pricing.</div>
              <div className="ai-tooltip-meta">
                {autosComparar.length} seleccionados
                {iaListo
                  ? <span style={{ color: "#22c55e", marginLeft: 6 }}>✓ Listo</span>
                  : <span style={{ color: "#fbbf24", marginLeft: 6 }}>Selecciona {iaFaltante} más</span>
                }
              </div>
              {!iaListo && <div className="ai-tooltip-hint">Selecciona al menos 2 modelos para comparar.</div>}
            </div>
          </div>
        </div>

        {/* Error IA */}
        {iaError && <div className="dl-error">⚠️ {iaError}</div>}

        {/* Tabla autos */}
        <div className="dl-table-card">
          <div className="dl-table-header">
            <span className="dl-table-title">
              {autos.length} auto{autos.length !== 1 ? 's' : ''} en la lista
              {autosComparar.length > 0 && ` · ${autosComparar.length} seleccionado${autosComparar.length !== 1 ? 's' : ''}`}
            </span>
            <button className="dl-btn" onClick={() => setTablaVisible(p => !p)}>
              {tablaVisible ? <><EyeOff size={13} /> Ocultar</> : <><Eye size={13} /> Mostrar</>}
            </button>
          </div>

          {tablaVisible && (
            cargandoLista ? (
              <div className="dl-table-empty"><CarLoader label="Cargando autos…" /></div>
            ) : autos.length === 0 ? (
              <div className="dl-table-empty">No hay autos en esta lista. Usa el buscador para agregar.</div>
            ) : (
              <div className="dl-table-wrap">
                <table className="dl-table">
                  <thead>
                    <tr>
                      <th>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                          <input type="checkbox" checked={autosComparar.length === autos.length && autos.length > 0} onChange={toggleSeleccionarTodos} />
                          Comparar
                        </label>
                      </th>
                      <th onClick={() => handleSort('marca')} style={{ cursor: 'pointer' }}>Marca{sortArrow('marca')}</th>
                      <th onClick={() => handleSort('model')} style={{ cursor: 'pointer' }}>Modelo{sortArrow('model')}</th>
                      <th>Versión</th>
                      <th onClick={() => handleSort('categoria')} style={{ cursor: 'pointer' }}>Categoría{sortArrow('categoria')}</th>
                      <th>Tipo motor</th>
                      <th style={{ width: 140 }}>Subcategoría</th>
                      <th>Origen</th>
                      <th onClick={() => handleSort('date_add')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Fecha{sortArrow('date_add')}</th>
                      {PRICE_TYPES.map(t => (
                        <th key={t} onClick={() => handleSort(`precio:${t}`)} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {t}{sortArrow(`precio:${t}`)}
                        </th>
                      ))}
                      <th style={{ width: 100 }}>Seguimiento</th>
                      <th style={{ width: 72 }}>Acción</th>
                      <th style={{ width: 90 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {autosOrdenados.map(auto => (
                      <tr key={auto.id} className={autosComparar.includes(auto.id) ? 'fila-seleccionada' : ''}>
                        <td>
                          <input type="checkbox" checked={autosComparar.includes(auto.id)} onChange={() => toggleCompararAuto(auto.id)} />
                        </td>
                        <td><span className="dl-marca-badge">{auto.marca}</span></td>
                        <td style={{ fontWeight: 500, color: '#111827' }}>{auto.model}</td>
                        <td style={{ color: '#6b7280' }}>
                          {auto.modelDetail}
                          {ultimoManual(auto) && (
                            <div className="dl-seg-badge" title="Tu último registro de seguimiento">
                              <Tag size={10} /> {ultimoManual(auto).tipoPrecio}: {clpFmt(ultimoManual(auto).precio)}
                            </div>
                          )}
                        </td>
                        <td>{auto.categoria ? <span className="badge-soft">{auto.categoria}</span> : '—'}</td>
                        <td style={{ color: '#6b7280' }}>{auto.tipomotor || '—'}</td>
                        <td>
                          {editandoSubcatId === auto.id ? (
                            <input
                              list="dl-subcat-sugeridas"
                              autoFocus
                              value={subcatInput}
                              onChange={e => setSubcatInput(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') guardarSubcategoria(auto.id); if (e.key === 'Escape') setEditandoSubcatId(null); }}
                              onBlur={() => guardarSubcategoria(auto.id)}
                              style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #c4b5fd', borderRadius: 5, width: 120 }}
                            />
                          ) : (
                            <span
                              onClick={() => iniciarEdicionSubcat(auto)}
                              title="Solo visible dentro de este proyecto. Click para editar."
                              style={{ cursor: 'pointer', fontSize: 12, color: subcategorias[auto.id] ? '#6d28d9' : '#d1d5db' }}
                            >
                              {subcategorias[auto.id] || '+ agregar'}
                            </span>
                          )}
                        </td>
                        <td style={{ color: '#6b7280' }}>{auto.origen || '—'}</td>
                        <td style={{ color: '#9ca3af', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {auto.date_add ? new Date(auto.date_add * 1000).toLocaleDateString('es-CL') : '—'}
                        </td>
                        {PRICE_TYPES.map(t => {
                          const val = getPrecioPorTipo(auto, t);
                          return <td key={t} style={{ whiteSpace: 'nowrap' }}>{val != null ? clpFmt(val) : '—'}</td>;
                        })}
                        <td>
                          <button className="dl-btn-seguimiento" onClick={() => abrirSeguimiento(auto)}>
                            <Clock size={12} /> {(seguimiento[auto.id]?.length) || 0}
                          </button>
                        </td>
                        <td>
                          <Link to={`/modelo/${auto.id}`} className="dl-action-btn" title="Ver detalles">
                            <SearchIcon size={13} strokeWidth={2} />
                          </Link>
                        </td>
                        <td>
                          <button className="dl-btn-quitar" onClick={() => quitarAutoDeLista(auto)}>
                            <Trash2 size={13} /> Quitar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <datalist id="dl-subcat-sugeridas">
                  {sugerenciasSubcategoria.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
            )
          )}
        </div>

        {/* Resultado IA */}
        {iaResultado?.response && (
          <div className="ai-result-card">
            <div className="ai-result-header">
              <div className="ai-result-title">📊 Análisis estratégico PM</div>
              {[...new Set(iaResultado.marcasAnalizadas || [])].map(m => (
                <span key={m} className="badge-pill">{m}</span>
              ))}
              {iaResultado.latencyMs && (
                <div className="ai-result-meta">
                  {(iaResultado.latencyMs / 1000).toFixed(1)}s
                  {iaResultado.usage && ` · ${iaResultado.usage.input + iaResultado.usage.output} tokens`}
                </div>
              )}
            </div>
            <div className="ai-result-md">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{iaResultado.response}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Comparativa */}
        {autosSeleccionados.length > 0 && (
          <>
            <div className="dl-comparador-bar">
              <span className="dl-comparador-label">
                <BarChart3 size={14} /> Tipo de precio:
              </span>
              <select
                className="dl-comparador-select"
                value={tipoComparado}
                onChange={e => setTipoComparado(e.target.value)}
              >
                <option value="Todos">Todos</option>
                {[...new Set(autosSeleccionados.flatMap(a => a.tiposprecio || []))].map((tipo, i) => (
                  <option key={`${tipo}-${i}`} value={tipo}>{tipo}</option>
                ))}
              </select>
            </div>

            <div className="dl-comp-table-wrap">
              <table className="dl-comp-table">
                <thead>
                  <tr>
                    <th>Marca</th><th>Modelo</th><th>Versión</th>
                    <th>Precio</th><th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {resumenUltimoPrecio.map(auto => (
                    <tr key={auto.id}>
                      <td><span className="dl-marca-badge">{auto.marca}</span></td>
                      <td style={{ fontWeight: 500 }}>{auto.model}</td>
                      <td style={{ color: '#6b7280' }}>{auto.modelDetail}</td>
                      <td><span className="dl-comp-precio">{clpFmt(auto.precio)}</span></td>
                      <td style={{ color: '#9ca3af', fontSize: 12 }}>{auto.fecha}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Gráfico */}
        {datosLinea.length > 0 && (
          <div className="dl-chart-card">
            <div className="dl-chart-header">
              <TrendingUp size={14} /> Evolución de precios · {tipoComparado}
            </div>
            <div className="dl-chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={datosLinea}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={v => `$${(v / 1_000_000).toFixed(1)}M`} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={v => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v)} />
                  <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 12 }} />
                  {Array.from(new Set(datosLinea.flatMap(row => Object.keys(row)))).filter(k => k !== 'fecha')
                    .map((modelo, i) => (
                      <Line key={modelo} type="monotone" dataKey={modelo} stroke={CHART_COLORS[i % 6]} strokeWidth={2} dot connectNulls />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Modal editar */}
      {modalVisible && (
        <div className="dl-modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="dl-modal" onClick={e => e.stopPropagation()}>
            <h3>Editar lista</h3>
            <input type="text" placeholder="Nombre" value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)} />
            <textarea placeholder="Descripción" value={nuevaDescripcion} onChange={e => setNuevaDescripcion(e.target.value)} />
            <div className="dl-modal-footer">
              <button className="dl-btn" onClick={() => setModalVisible(false)}>
                <X size={13} /> Cancelar
              </button>
              <button className="dl-btn dl-btn--primary" onClick={guardarCambiosModal}>
                <Save size={13} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal seguimiento de precios */}
      {seguimientoAuto && (
        <div className="dl-modal-overlay" onClick={cerrarSeguimiento}>
          <div className="dl-modal dl-modal--seguimiento" onClick={e => e.stopPropagation()}>
            <h3>Seguimiento de precios</h3>
            <p className="dl-seg-sub">
              {seguimientoAuto.marca} {seguimientoAuto.model} {seguimientoAuto.modelDetail} — privado, no afecta los datos generales del catálogo.
            </p>

            <div className="dl-seg-table-wrap">
              <table className="dl-seg-table">
                <thead>
                  <tr><th>Fecha</th><th>Tipo</th><th>Precio</th><th>Nota</th><th></th></tr>
                </thead>
                <tbody>
                  {!(seguimiento[seguimientoAuto.id]?.length) ? (
                    <tr><td colSpan={5} className="dl-seg-empty">Sin registros todavía.</td></tr>
                  ) : (
                    [...seguimiento[seguimientoAuto.id]].sort((a, b) => b.fecha.localeCompare(a.fecha)).map(e => (
                      <tr key={e.id}>
                        <td>{new Date(`${e.fecha}T00:00:00`).toLocaleDateString('es-CL')}</td>
                        <td>{e.tipoPrecio}</td>
                        <td>{clpFmt(e.precio)}</td>
                        <td style={{ color: '#6b7280' }}>{e.nota || '—'}</td>
                        <td>
                          <button className="dl-btn-quitar" onClick={() => eliminarEntradaSeguimiento(e.id)} title="Eliminar registro">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="dl-seg-form">
              <input
                list="dl-seg-tipos-sugeridos"
                placeholder="Tipo de precio"
                value={segTipo}
                onChange={e => setSegTipo(e.target.value)}
              />
              <datalist id="dl-seg-tipos-sugeridos">
                {(seguimientoAuto.tiposprecio || []).map(t => <option key={t} value={t} />)}
              </datalist>
              <input type="number" placeholder="Precio" value={segPrecio} onChange={e => setSegPrecio(e.target.value)} />
              <input type="date" value={segFecha} onChange={e => setSegFecha(e.target.value)} />
              <input
                className="dl-seg-nota"
                type="text"
                placeholder="Nota (opcional) — ej: visto en tal dealer"
                value={segNota}
                onChange={e => setSegNota(e.target.value)}
              />
            </div>

            <div className="dl-modal-footer">
              <button className="dl-btn" onClick={cerrarSeguimiento}>
                <X size={13} /> Cerrar
              </button>
              <button className="dl-btn dl-btn--primary" onClick={agregarEntradaSeguimiento} disabled={segGuardando}>
                <Plus size={13} /> Agregar registro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DetalleLista;